import { useCreateProfil, useDroits, useProfil, useUpdateProfil } from "@repo/ui/hooks/api/index";
import { ProfilFormData } from "@repo/ui/lib/api/types/profil";
import { useEffect, useState } from "react";

interface UseProfilFormProps {
  id?: string;
  profilId?: number;
  onSuccess: () => void;
}

export function useProfilForm({ id, profilId, onSuccess }: UseProfilFormProps) {
  // Convert id string to profilId number if provided
  const effectiveProfilId = id ? parseInt(id, 10) : profilId;
  const [formData, setFormData] = useState<ProfilFormData>({
    name: "",
    groupe_type: "Admin",
    droit_id: 1,
    is_init: true,
  });
  
  const [error, setError] = useState<string | null>(null);
  
  const { data: profilData, isLoading: isLoadingProfil } = useProfil(effectiveProfilId || 0);
  const { data: droitOptions, isLoading: isLoadingDroits } = useDroits();
  const createProfil = useCreateProfil();
  const updateProfil = useUpdateProfil();
  const isEditing = !!effectiveProfilId;
  
  useEffect(() => {
    // When the component mounts or the profilId changes, reset the form
    if (!isEditing) {
      setFormData({
        name: "",
        groupe_type: "Admin",
        droit_id: 1,
        is_init: true,
      });
    }
  }, [isEditing, effectiveProfilId]);

  useEffect(() => {
    if (isEditing && profilData?.data) {
      setFormData({
        name: profilData.data.name,
        groupe_type: profilData.data.groupe_type || "ADMIN",
        droit_id: profilData.data.droit_id,
        is_init: profilData.data.is_init,
        deleted: profilData.data.deleted,
      });
    }
  }, [isEditing, profilData, effectiveProfilId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    // Convert droit_id to number, keep other values as string
    const finalValue = name === 'droit_id' ? parseInt(value, 10) : value;
    setFormData({
      ...formData,
      [name]: finalValue,
    });
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData({
      ...formData,
      [name]: checked,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      if (isEditing && effectiveProfilId) {
        await updateProfil.mutateAsync({
          id: effectiveProfilId,
          //@ts-ignore
          data: formData,
        });
      } else {
        await createProfil.mutateAsync(formData);
      }
      onSuccess();
    } catch (err) {
      setError("Une erreur est survenue lors de l'enregistrement du profil.");
      console.error(err);
    }
  };

  const isLoading = (isEditing && isLoadingProfil) || isLoadingDroits;
  const isPending = createProfil.isPending || updateProfil.isPending;

  return {
    formData,
    error,
    droitOptions,
    isEditing,
    isLoading,
    isLoadingDroits,
    isPending,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    handleSubmit,
  };
}
